/**
 * AddressInput Component with Google Maps Autocomplete
 * 
 * Automatically enables Google Maps Places Autocomplete when API key is configured.
 * Falls back to basic text input if no API key is available.
 * 
 * Priority for API key:
 * 1. Environment variable (REACT_APP_GOOGLE_MAPS_API_KEY)
 * 2. Platform settings from database (Super Admin Dashboard config)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { MapPin, Loader2, Search } from 'lucide-react';
import { useDebouncedCallback } from 'use-debounce';
import axios from 'axios';

// Check if Google Maps API key exists in environment
const ENV_GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const API_URL = process.env.REACT_APP_BACKEND_URL;

const AddressInput = ({ 
  value = '', 
  onChange, 
  onAddressSelected,
  label = "Address",
  placeholder = "Enter street address, city, state, zip",
  required = false,
  disabled = false,
  error,
  multiline = false,
  className = "",
  restrictToCountry = 'us', // Default to US for healthcare context
  clinicId = null, // Optional clinic ID for clinic-specific settings
  ...props 
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [autocompleteService, setAutocompleteService] = useState(null);
  const [placesService, setPlacesService] = useState(null);
  const [googleMapsApiKey, setGoogleMapsApiKey] = useState(ENV_GOOGLE_MAPS_API_KEY || null);
  const [keyFetched, setKeyFetched] = useState(!!ENV_GOOGLE_MAPS_API_KEY);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Sync external value changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Fetch Google Maps API key from platform settings if not in environment
  useEffect(() => {
    const fetchApiKey = async () => {
      if (ENV_GOOGLE_MAPS_API_KEY) {
        setGoogleMapsApiKey(ENV_GOOGLE_MAPS_API_KEY);
        setKeyFetched(true);
        return;
      }

      try {
        const url = clinicId 
          ? `${API_URL}/api/platform-settings/google-maps/config?clinic_id=${clinicId}`
          : `${API_URL}/api/platform-settings/google-maps/config`;
        
        const response = await axios.get(url);
        
        if (response.data.enabled && response.data.api_key) {
          setGoogleMapsApiKey(response.data.api_key);
        }
      } catch (error) {
        console.log('Google Maps API key not configured:', error.message);
      } finally {
        setKeyFetched(true);
      }
    };

    fetchApiKey();
  }, [clinicId]);

  // Load Google Maps script dynamically
  useEffect(() => {
    if (!googleMapsApiKey || !keyFetched) return;

    // Check if already loaded
    if (window.google?.maps?.places) {
      setMapsLoaded(true);
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => setMapsLoaded(true));
      return;
    }

    // Load the script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => setMapsLoaded(true);
    script.onerror = () => console.error('Failed to load Google Maps script');
    document.head.appendChild(script);

    return () => {
      // Cleanup not needed as we want to keep the script loaded
    };
  }, [googleMapsApiKey, keyFetched]);

  // Initialize services when Maps is loaded
  useEffect(() => {
    if (!mapsLoaded || !window.google?.maps?.places) return;

    setAutocompleteService(new window.google.maps.places.AutocompleteService());
    
    // Create a hidden div for PlacesService (requires a map or attributions element)
    const attributionDiv = document.createElement('div');
    setPlacesService(new window.google.maps.places.PlacesService(attributionDiv));
  }, [mapsLoaded]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced fetch suggestions
  const fetchSuggestions = useDebouncedCallback(async (input) => {
    if (!autocompleteService || input.length < 3) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const request = {
        input,
        types: ['address'],
        componentRestrictions: restrictToCountry ? { country: restrictToCountry } : undefined,
      };

      autocompleteService.getPlacePredictions(request, (predictions, status) => {
        setIsLoading(false);
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSuggestions(predictions);
          setIsOpen(true);
        } else {
          setSuggestions([]);
        }
      });
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setIsLoading(false);
      setSuggestions([]);
    }
  }, 300);

  const handleInputChange = useCallback((e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    if (onChange) {
      onChange(e);
    }

    // Fetch suggestions if Google Maps is available
    if (autocompleteService && newValue.length >= 3) {
      fetchSuggestions(newValue);
    } else {
      setSuggestions([]);
      setIsOpen(false);
    }

    // Basic callback without structured data
    if (onAddressSelected && !autocompleteService) {
      onAddressSelected({
        formatted_address: newValue,
        street: '',
        city: '',
        state: '',
        zip: '',
        country: 'US',
        lat: null,
        lng: null,
        place_id: null
      });
    }
  }, [onChange, onAddressSelected, autocompleteService, fetchSuggestions]);

  const handleSelectSuggestion = useCallback(async (prediction) => {
    if (!placesService) return;

    setInputValue(prediction.description);
    setIsOpen(false);
    setSuggestions([]);

    // Get place details including lat/lng
    placesService.getDetails(
      {
        placeId: prediction.place_id,
        fields: ['formatted_address', 'geometry', 'address_components', 'place_id']
      },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          const addressData = {
            formatted_address: place.formatted_address,
            place_id: place.place_id,
            lat: place.geometry?.location?.lat() || null,
            lng: place.geometry?.location?.lng() || null,
            street: '',
            city: '',
            state: '',
            zip: '',
            country: ''
          };

          // Parse address components
          place.address_components?.forEach(component => {
            const types = component.types;
            
            if (types.includes('street_number')) {
              addressData.street = component.long_name + ' ';
            }
            if (types.includes('route')) {
              addressData.street += component.long_name;
            }
            if (types.includes('locality')) {
              addressData.city = component.long_name;
            }
            if (types.includes('administrative_area_level_1')) {
              addressData.state = component.short_name;
            }
            if (types.includes('postal_code')) {
              addressData.zip = component.long_name;
            }
            if (types.includes('country')) {
              addressData.country = component.short_name;
            }
          });

          // Update input value
          setInputValue(addressData.formatted_address);

          // Call parent onChange
          if (onChange) {
            onChange({ target: { value: addressData.formatted_address, name: props.name || 'address' } });
          }

          // Call structured data callback
          if (onAddressSelected) {
            onAddressSelected(addressData);
          }
        }
      }
    );
  }, [placesService, onChange, onAddressSelected, props.name]);

  // Render with Google Maps Autocomplete
  if (googleMapsApiKey && keyFetched) {
    return (
      <div className="space-y-2" ref={containerRef}>
        {label && (
          <Label htmlFor={props.id || 'address'}>
            <span className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-teal-600" />
              {label}
              {required && <span className="text-red-500 ml-1">*</span>}
            </span>
          </Label>
        )}
        
        <div className="relative">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              ref={inputRef}
              id={props.id || 'address'}
              name={props.name || 'address'}
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onFocus={() => suggestions.length > 0 && setIsOpen(true)}
              placeholder={placeholder}
              required={required}
              disabled={disabled}
              className={`pl-10 ${className}`}
              autoComplete="off"
              {...props}
            />
            {isLoading && (
              <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
            )}
          </div>

          {/* Suggestions dropdown */}
          {isOpen && suggestions.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion.place_id}
                  type="button"
                  className="w-full px-4 py-3 text-left hover:bg-teal-50 focus:bg-teal-50 focus:outline-none transition-colors border-b border-gray-100 last:border-b-0"
                  onClick={() => handleSelectSuggestion(suggestion)}
                >
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium text-gray-900 text-sm">
                        {suggestion.structured_formatting?.main_text || suggestion.description.split(',')[0]}
                      </div>
                      <div className="text-xs text-gray-500">
                        {suggestion.structured_formatting?.secondary_text || suggestion.description}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
              <div className="px-3 py-2 text-xs text-gray-400 bg-gray-50 border-t flex items-center gap-1">
                <img 
                  src="https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3_hdpi.png" 
                  alt="Powered by Google" 
                  className="h-3"
                />
              </div>
            </div>
          )}
        </div>
        
        {error && (
          <p className="text-sm text-red-500">{error}</p>
        )}
        
        {!mapsLoaded && !disabled && (
          <p className="text-xs text-amber-600">
            ⏳ Loading Google Maps...
          </p>
        )}
      </div>
    );
  }

  // Fallback: Basic input (no autocomplete)
  const InputComponent = multiline ? Textarea : Input;

  return (
    <div className="space-y-2">
      {label && (
        <Label htmlFor={props.id || 'address'}>
          <span className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-gray-400" />
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </span>
        </Label>
      )}
      
      <InputComponent
        id={props.id || 'address'}
        name={props.name || 'address'}
        value={inputValue}
        onChange={handleInputChange}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        className={className}
        rows={multiline ? 3 : undefined}
        {...props}
      />
      
      {error && (
        <p className="text-sm text-red-500">{error}</p>
      )}
      
      {/* Info message about autocomplete - only show if key was not found */}
      {!disabled && keyFetched && !googleMapsApiKey && (
        <p className="text-xs text-gray-500">
          💡 Add a Google Maps API key in Super Admin Dashboard → Integrations to enable address autocomplete
        </p>
      )}
      
      {/* Loading message while fetching API key */}
      {!keyFetched && !disabled && (
        <p className="text-xs text-amber-600">
          ⏳ Checking for address autocomplete...
        </p>
      )}
    </div>
  );
};

export default AddressInput;
